"use client";
import { useMemo } from "react";
import { cn } from "@/lib/utils";
import formatProgramPrice from "@/lib/checkout-geo-pricing/formatProgramPrice";
import { ProgramPricingData } from "@/lib/ecommerce/commercetools/client-only/getProgramPricingData";
import { useCurrency } from "@/contexts/UserPreferencesContext/hooks/useCurrency";
import { DEFAULT_CURRENCY } from "@/lib/currency/constants";
import { useProgramRedesignConfig } from "@/monarch/rules/programRedesignTraffic/useProgramRedesignTraffic";
import { useCytCurrencyConfig } from "@/lib/currency/useCytCurrencyConfig";
import useTranslations from "@/i18n/useTranslations";

type ProgramPricingProps = {
  pricingData: Partial<ProgramPricingData>;
  originalPriceSize?: string;
  discountedPriceSize?: string;
  strikethroughClass?: string;
  discountedPriceClassName?: string;
  originalPriceClassName?: string;
  hideCurrencyAbbreviation?: boolean;
  stackBigPrices?: boolean;
  stackPricesInBanner?: boolean;
  priceGap?: "default" | "none" | "sm" | "xs";
};
const priceGapClasses = {
  default: "gap-4",
  none: "",
  sm: "gap-2",
  xs: "gap-1",
};
export default function ProgramPricing({
  pricingData,
  strikethroughClass = "line-through",
  originalPriceSize,
  discountedPriceSize,
  discountedPriceClassName,
  originalPriceClassName,
  hideCurrencyAbbreviation,
  stackBigPrices = true,
  stackPricesInBanner = false,
  priceGap = "default",
}: ProgramPricingProps) {
  const t = useTranslations("Common");
  const { getConvertedProductPrice } = useCurrency();
  const {
    isLoading: programConfigLoading,
    currencySelector: programCurrencySelector,
  } = useProgramRedesignConfig();
  const needsCytCurrencyFallback =
    !programConfigLoading && programCurrencySelector == null;
  const { currencySelector: cytCurrencySelector } = useCytCurrencyConfig({
    enabled: needsCytCurrencyFallback,
  });
  const currencySelector = programCurrencySelector ?? cytCurrencySelector;

  const originalPriceInput = pricingData?.originalPrice;
  const discountedPriceInput = pricingData?.discountedPrice;

  // Convert a single "anchor" price through the shared hook -- which caches
  // by (currency, price) and prefers a locked rate when one exists. The
  // anchor is the original price when present, otherwise the discounted
  // price. Converting one price and scaling the other by the discount ratio
  // avoids a second, uncached request. Mirrors CurrencyInlinePill/
  // CytSyncedPricing.
  const anchorPrice =
    originalPriceInput && originalPriceInput > 0
      ? originalPriceInput
      : discountedPriceInput;

  const anchorConversion = useMemo(
    () =>
      anchorPrice && anchorPrice > 0
        ? getConvertedProductPrice(anchorPrice, currencySelector)
        : null,
    [anchorPrice, currencySelector, getConvertedProductPrice],
  );

  const convertedAnchor =
    anchorConversion && !anchorConversion?.isLoading
      ? (anchorConversion?.convertedPrice ?? null)
      : null;

  const convertedOriginalPrice =
    originalPriceInput && originalPriceInput > 0 ? convertedAnchor : null;

  const convertedDiscountedPrice = useMemo(() => {
    if (
      !discountedPriceInput ||
      discountedPriceInput <= 0 ||
      convertedAnchor == null
    ) {
      return null;
    }
    // No original price: the anchor already is the converted discounted price.
    if (!originalPriceInput || originalPriceInput <= 0) {
      return convertedAnchor;
    }
    // Both present: scale the discounted price by the same conversion ratio.
    return (convertedAnchor * discountedPriceInput) / originalPriceInput;
  }, [convertedAnchor, discountedPriceInput, originalPriceInput]);

  const selectedCurrency =
    anchorConversion?.displayCurrency.code ?? DEFAULT_CURRENCY;

  const convertedPricingData = useMemo(() => {
    if (!pricingData) return null;

    if (currencySelector) {
      return {
        ...pricingData,
        originalPrice:
          convertedOriginalPrice !== null &&
          convertedOriginalPrice !== undefined
            ? convertedOriginalPrice
            : pricingData.originalPrice,
        discountedPrice:
          convertedDiscountedPrice !== null &&
          convertedDiscountedPrice !== undefined
            ? convertedDiscountedPrice
            : pricingData.discountedPrice,
        currency: selectedCurrency,
      };
    } else {
      return {
        ...pricingData,
        originalPrice: pricingData.originalPrice,
        discountedPrice: pricingData.discountedPrice,
        currency: DEFAULT_CURRENCY,
      };
    }
  }, [
    pricingData,
    convertedOriginalPrice,
    convertedDiscountedPrice,
    selectedCurrency,
    currencySelector,
  ]);

  const bigPrice =
    convertedOriginalPrice &&
    convertedDiscountedPrice &&
    (convertedOriginalPrice > 9999 || convertedDiscountedPrice > 9999);
  const isOneColumnLayout = (stackBigPrices && bigPrice) || stackPricesInBanner;
  const layoutClass = isOneColumnLayout
    ? "flex flex-col"
    : `flex flex-row ${priceGapClasses[priceGap]}`;
  const alignmentClass =
    isOneColumnLayout && !stackPricesInBanner ? "items-center" : "";

  const { originalPrice, discountedPrice, currency } =
    convertedPricingData || pricingData;
  return (
    <div className={cn(layoutClass, alignmentClass)}>
      {originalPrice && discountedPrice && originalPrice !== discountedPrice ? (
        // Both prices are available and different, show original with line-through
        <div
          className={cn(
            "opacity-50 whitespace-nowrap",
            strikethroughClass,
            `${originalPriceSize ? `text-${originalPriceSize}` : ""}`,
            originalPriceClassName,
            stackPricesInBanner && bigPrice ? "text-2xl md:text-3xl" : "",
          )}
        >
          <span className="sr-only">{t("originalPrice")}</span>
          {`${formatProgramPrice(originalPrice, currency)}${!hideCurrencyAbbreviation ? ` ${currency}` : ""}`}
        </div>
      ) : null}
      {discountedPrice && originalPrice !== discountedPrice ? (
        <div
          className={cn(
            "whitespace-nowrap",
            `${discountedPriceSize ? `text-${discountedPriceSize}` : ""}`,
            discountedPriceClassName,
            stackPricesInBanner && bigPrice ? "text-2xl md:text-3xl" : "",
          )}
        >
          <span className="sr-only">
            {originalPrice ? t("discountedPrice") : t("priceLabel")}
          </span>
          {`${formatProgramPrice(discountedPrice, currency)}${!originalPrice && !hideCurrencyAbbreviation ? ` ${currency}` : ""}`}
        </div>
      ) : originalPrice &&
        (!discountedPrice || originalPrice === discountedPrice) ? (
        // Only original price available OR prices are equal, show single price without line-through
        <div
          className={cn(
            "whitespace-nowrap",
            `${originalPriceSize ? `text-${originalPriceSize}` : ""}`,
            originalPriceClassName,
            stackPricesInBanner && bigPrice ? "text-2xl md:text-3xl" : "",
          )}
        >
          <span className="sr-only">{t("priceLabel")}</span>
          {`${formatProgramPrice(originalPrice, currency)}${!hideCurrencyAbbreviation ? ` ${currency}` : ""}`}
        </div>
      ) : null}
    </div>
  );
}
